import PropTypes from 'prop-types'
import React from 'react'
import {connect} from 'react-redux'

import FirmActions from '../../../actions/FirmActions'

import {ControlButton, Cancel, Continue} from '../../../../../shared_components/ButtonTypes'
import EditableItemsPicker from './EditableItemsPicker'
import Modal from '../../../../../shared_components/Modal'
import modalWrapper from '../../../../../shared_components/ModalWrapper'
import ShadowHeader from '../ShadowHeader'
import StatusToggle from './StatusToggle'

import {selectAllItemIds} from '../../../../../lib/plan_data/itemSelectors'

import './editFirmConfig.scss'

import ContactAccountManagerNote from './ContactAccountManagerNote'
import ShadowWrapper from '../ShadowWrapper'

export class EditWriteAccess extends React.Component {
  constructor(props) {
    super(props)

    // Destructure with a +{}+ fallback rather than relying on +defaultProps+ alone:
    // +defaultProps+ on the class isn't hoisted onto the +connect()+/+modalWrapper+
    // wrappers, so a caller that omits +writeAccess+ on the default export would
    // otherwise blow up here on +.write_access+ / +.editable_item_ids+ access.
    const {write_access = false, editable_item_ids = null} = this.props.writeAccess || {}

    this.state = {
      isEnabled: write_access,
      editableItemIds: editable_item_ids
    }

    this.resetWriteAccessState = this.resetWriteAccessState.bind(this)
    this.saveWriteAcess = this.saveWriteAcess.bind(this)
    this.toggleWriteAccess = this.toggleWriteAccess.bind(this)
    this.setEditableItemIds = this.setEditableItemIds.bind(this)
  }
  toggleWriteAccess() {
    this.setState({isEnabled: !this.state.isEnabled})
  }
  setEditableItemIds(editableItemIds) {
    this.setState({editableItemIds})
  }
  // Treat "every metadata item is checked" as the unset state so the DB can keep +nil+
  // (which means "no restriction, including any future-added items"). Anything narrower is
  // an explicit allowlist. We compare via a Set + every() rather than just length so a
  // selection containing stale/unknown ids can never be silently widened to "no preset".
  resolvedEditableItemIds() {
    const {editableItemIds} = this.state
    const allItemIds = this.props.allItemIds || []
    if (editableItemIds == null) return null
    if (!allItemIds.length) return editableItemIds
    const selected = new Set(editableItemIds)
    const allSelected = selected.size === allItemIds.length &&
      allItemIds.every(id => selected.has(id))
    return allSelected ? null : editableItemIds
  }
  // Per the AC: AMs cannot save Permission to Edit as Enabled without leaving at
  // least one item selected, so disable Save while the picker selection is an
  // explicit empty array. +editableItemIds == null+ is the "no preset" state and
  // means the firm has full access — that's allowed.
  saveDisabled() {
    if (!this.state.isEnabled) return false
    const {editableItemIds} = this.state
    return Array.isArray(editableItemIds) && editableItemIds.length === 0
  }
  saveWriteAcess() {
    const firmConfig = {write_access: this.state.isEnabled}
    // When write access is being turned off, leave +editable_item_ids+ out of the payload so
    // any previously-saved preset is preserved for the next time it gets turned back on.
    // When write access is on, the picker is the source of truth — always send its current
    // selection so a no-op save can't silently leave the DB out of sync with what the AM sees.
    if (this.state.isEnabled) {
      firmConfig.editable_item_ids = this.resolvedEditableItemIds()
    }

    FirmActions.updateFirmConfig({
      firmId: this.props.firmId,
      data: {firm_config: firmConfig}
    })
    this.context.closeModal()
  }
  resetWriteAccessState() {
    const {write_access = false, editable_item_ids = null} = this.props.writeAccess || {}
    this.setState({
      isEnabled: write_access,
      editableItemIds: editable_item_ids
    })
    this.context.closeModal()
  }

  render() {
    // Mirror the constructor's defensive destructure here too — the +connect()+ /
    // +modalWrapper+ wrappers don't hoist +defaultProps+, so a caller that omits
    // +writeAccess+ on the default export would otherwise blow up on the direct
    // +.write_access+ access below.
    const {write_access: writeAccessEnabled = false} = this.props.writeAccess || {}
    return (
      <ShadowWrapper>
        <div className='settings row-wrapper'>
          <p>Status: </p>
          <div className='column-wrapper'>
            <p>{writeAccessEnabled ? 'Enabled' : 'Disabled'}</p>
            <p className='description'>{writeAccessEnabled ?
              "Firm Users can request permission to edit a portion of a client's Everplan. Clients can grant permission through their account settings or when signing up." :
              "Firm Users cannot edit a client's Everplan once the client has activated their account. Clients cannot grant permission to Firm Users."}</p>
            {
              this.props.isFirmActive && this.props.isAccountManager &&
              (
                <div className='edit-config'>
                  <ControlButton faClass='fa-pencil-square-o' onClick={this.context.showModal} buttonText='Edit' />
                  <Modal visible={this.props.showModal}>
                    <ShadowHeader headerText='Permission to Edit' />
                    <div className='config-wrapper'>
                      <StatusToggle
                        description={{
                          disabled: "Firm Users cannot edit a client's Everplan once the client has activated their account. Clients cannot grant permission to Firm Users.",
                          enabled: "Firm Users can request permission to edit a portion of a client's Everplan. Clients can grant permission through their account settings or when signing up."
                        }}
                        enabledChecked={this.state.isEnabled}
                        name='write_access'
                        onChange={this.toggleWriteAccess}
                        statusText={{
                          disabled: 'Disabled',
                          enabled: 'Enabled'
                        }}
                      />
                      {
                        this.state.isEnabled && (
                          <div className='editable-items-section'>
                            <EditableItemsPicker
                              value={this.state.editableItemIds}
                              onChange={this.setEditableItemIds}
                            />
                          </div>
                        )
                      }
                      <div className='button-group'>
                        <Cancel onClick={this.resetWriteAccessState} />
                        <Continue
                          loading={this.props.processing}
                          disabled={this.saveDisabled()}
                          onClick={this.saveWriteAcess}>
                          Save
                        </Continue>
                      </div>
                    </div>
                  </Modal>
                </div>
              )
            }
            <ContactAccountManagerNote isAccountManager={this.props.isAccountManager} />
          </div>
        </div>
      </ShadowWrapper>
    )
  }
}

EditWriteAccess.contextTypes = {
  showModal: PropTypes.func,
  closeModal: PropTypes.func,
  modalClosed: PropTypes.func
}

EditWriteAccess.defaultProps = {
  writeAccess: {},
  showModal: false
}

EditWriteAccess.propTypes = {
  statusText: PropTypes.string,
  isAccountManager: PropTypes.bool,
  firmId: PropTypes.string.isRequired,
  description: PropTypes.string,
  writeAccess: PropTypes.shape({
    write_access: PropTypes.bool,
    // `null` means "no preset configured" — the firm has full edit access by default.
    editable_item_ids: PropTypes.oneOfType([
      PropTypes.arrayOf(PropTypes.string),
      PropTypes.oneOf([null])
    ])
  }),
  showModal: PropTypes.bool,
  isFirmActive: PropTypes.bool,
  processing: PropTypes.bool,
  allItemIds: PropTypes.arrayOf(PropTypes.string)
}

const mapStateToProps = state => ({
  allItemIds: selectAllItemIds(state)
})

// `connect` wraps the class itself so the redux-injected +allItemIds+ prop is delivered
// directly to +EditWriteAccess+, then +modalWrapper+ adds the modal context around the
// already-connected component.
export default modalWrapper(connect(mapStateToProps)(EditWriteAccess))
